import {
  Application,
  Assets,
  BitmapText,
  Container,
  Filter,
  GlProgram,
  Sprite,
  Texture,
  TilingSprite,
} from 'pixi.js';
import {
  BossKind,
  EnemyState,
  GameMode,
  INTERNAL_HEIGHT,
  INTERNAL_WIDTH,
  Owner,
  POOL_CAPACITY,
  ProjectileFlags,
  ProjectileKind,
} from '../../constants';
import { PURCHASES, PurchaseState, getPurchaseState, purchaseCost } from '../../data/purchases';
import type { GameWorld } from '../../world/GameWorld';
import filterVertex from '../../shaders/filterVertex.glsl?raw';
import lineFragment from '../../shaders/lineScroll.frag.glsl?raw';
import paletteFragment from '../../shaders/paletteLookup.frag.glsl?raw';

const PLAYER_TEXTURES = [
  ['player_up_0', 'player_up_1'],
  ['player_level_0', 'player_level_1'],
  ['player_down_0', 'player_down_1'],
] as const;
const NODE_TEXTURES = ['node_0', 'node_1', 'node_2'] as const;
const NODE_DISABLED_TEXTURE = 'node_disabled';
const ENEMY_TEXTURES = [
  ['enemy_threadling_up_0', 'enemy_threadling_up_1', 'enemy_threadling_level_0', 'enemy_threadling_level_1', 'enemy_threadling_down_0', 'enemy_threadling_down_1'],
  ['enemy_plate_fin_up_0', 'enemy_plate_fin_up_1', 'enemy_plate_fin_level_0', 'enemy_plate_fin_level_1', 'enemy_plate_fin_down_0', 'enemy_plate_fin_down_1'],
  ['enemy_indexer_up_0', 'enemy_indexer_up_1', 'enemy_indexer_level_0', 'enemy_indexer_level_1', 'enemy_indexer_down_0', 'enemy_indexer_down_1'],
  ['enemy_shade_polyp_up_0', 'enemy_shade_polyp_up_1', 'enemy_shade_polyp_level_0', 'enemy_shade_polyp_level_1', 'enemy_shade_polyp_down_0', 'enemy_shade_polyp_down_1'],
  ['enemy_mirror_wisp_up_0', 'enemy_mirror_wisp_up_1', 'enemy_mirror_wisp_level_0', 'enemy_mirror_wisp_level_1', 'enemy_mirror_wisp_down_0', 'enemy_mirror_wisp_down_1'],
  ['enemy_lock_beetle_up_0', 'enemy_lock_beetle_up_1', 'enemy_lock_beetle_level_0', 'enemy_lock_beetle_level_1', 'enemy_lock_beetle_down_0', 'enemy_lock_beetle_down_1'],
  ['enemy_fold_ray_up_0', 'enemy_fold_ray_up_1', 'enemy_fold_ray_level_0', 'enemy_fold_ray_level_1', 'enemy_fold_ray_down_0', 'enemy_fold_ray_down_1'],
  ['enemy_archive_ram_up_0', 'enemy_archive_ram_up_1', 'enemy_archive_ram_level_0', 'enemy_archive_ram_level_1', 'enemy_archive_ram_down_0', 'enemy_archive_ram_down_1'],
  ['enemy_choir_engine_up_0', 'enemy_choir_engine_up_1', 'enemy_choir_engine_level_0', 'enemy_choir_engine_level_1', 'enemy_choir_engine_down_0', 'enemy_choir_engine_down_1'],
  ['enemy_prism_eater_up_0', 'enemy_prism_eater_up_1', 'enemy_prism_eater_level_0', 'enemy_prism_eater_level_1', 'enemy_prism_eater_down_0', 'enemy_prism_eater_down_1'],
] as const;
const PROJECTILE_TEXTURES = [
  'projectile_pulse',
  'projectile_arc',
  'projectile_prism',
  'projectile_lattice',
  'projectile_node_focus',
  'projectile_enemy_orb',
  'projectile_enemy_needle',
  'projectile_beam',
  'projectile_enemy_lock',
  'projectile_reflected',
  'projectile_phase',
] as const;
const NODE_PROJECTILE_TEXTURES = ['projectile_node_focus', 'projectile_node_fan', 'projectile_node_mirror', 'projectile_node_tether'] as const;
const EXPLOSION_TEXTURES = ['explosion_0', 'explosion_1', 'explosion_2', 'explosion_3', 'explosion_4', 'explosion_5', 'explosion_6', 'explosion_7'] as const;
const LUX_TEXTURES = ['pickup_lux_0', 'pickup_lux_1'] as const;
const REPAIR_TEXTURES = ['pickup_repair_0', 'pickup_repair_1'] as const;
const CATALOGUE_JAW_TEXTURES = ['boss_catalogue_jaw_0', 'boss_catalogue_jaw_1'] as const;
const CATALOGUE_CORE_TEXTURES = ['boss_catalogue_core_0', 'boss_catalogue_core_1'] as const;
const NULL_SCRIBE_CORE_TEXTURES = ['boss_null_scribe_core_0', 'boss_null_scribe_core_1'] as const;
const NULL_SCRIBE_QUILL_TEXTURES = ['boss_null_scribe_quill_0', 'boss_null_scribe_quill_1'] as const;
const MNEMOSYNE_CORE_TEXTURES = ['boss_mnemosyne_core_0', 'boss_mnemosyne_core_1'] as const;
const MNEMOSYNE_PART_TEXTURES = ['boss_mnemosyne_part_0', 'boss_mnemosyne_part_1'] as const;

const STAGE_ART = [
  { far: 'bg_glass_far', middle: 'bg_glass_mid', near: 'bg_glass_near', middleAlpha: 0.58, nearAlpha: 0.88 },
  { far: 'bg_garden_far', middle: 'bg_garden_mid', near: 'bg_garden_near', middleAlpha: 0.5, nearAlpha: 0.82 },
  { far: 'bg_city_far', middle: 'bg_city_mid', near: 'bg_city_near', middleAlpha: 0.54, nearAlpha: 0.86 },
  { far: 'bg_register_far', middle: 'bg_register_mid', near: 'bg_register_near', middleAlpha: 0.48, nearAlpha: 0.9 },
] as const;

const SPRITE_ATLAS_URL = 'assets/lumina-atlas.json';
const BACKGROUND_ATLAS_URL = 'assets/lumina-backgrounds.json';
const FONT_URL = 'assets/lumina-font.fnt';
const ANGLE_STEP = Math.PI / 16;

export interface RendererSettings {
  shake: number;
  flashReduction: boolean;
}

interface AtlasSheet {
  readonly textures: Record<string, Texture>;
  readonly textureSource?: { scaleMode: string };
}

export class PixiRenderer {
  private app: Application | null = null;
  private disposed = false;
  private sheet: AtlasSheet | null = null;
  private backgroundSheet: AtlasSheet | null = null;
  private root: Container | null = null;
  private background: Container | null = null;
  private gameLayer: Container | null = null;
  private effectsLayer: Container | null = null;
  private calibrationLayer: Container | null = null;
  private far: TilingSprite | null = null;
  private middle: TilingSprite | null = null;
  private near: TilingSprite | null = null;
  private erasure: Sprite | null = null;
  private flash: Sprite | null = null;
  private player: Sprite | null = null;
  private readonly nodes: Sprite[] = [];
  private readonly enemies: Sprite[] = [];
  private readonly projectiles: Sprite[] = [];
  private readonly particles: Sprite[] = [];
  private readonly pickups: Sprite[] = [];
  private readonly bossParts: Sprite[] = [];
  private readonly bossLines: Sprite[] = [];
  private readonly tetherLines: Sprite[] = [];
  private lineFilter: Filter | null = null;
  private paletteFilter: Filter | null = null;
  private gateHighlight: Sprite | null = null;
  private gateTitle: BitmapText | null = null;
  private gateLux: BitmapText | null = null;
  private gateMessage: BitmapText | null = null;
  private readonly gateNames: BitmapText[] = [];
  private readonly gatePrices: BitmapText[] = [];
  private gateCacheSelection = -1;
  private gateCacheLux = -1;
  private gateCacheHash = -1;
  private gateCacheMessageState = -1;
  private currentStageTexture = -1;
  private settings: RendererSettings = { shake: 1, flashReduction: false };
  rendererName = 'uninitialized';

  async init(canvas: HTMLCanvasElement): Promise<void> {
    if (this.disposed) return;
    const app = new Application();
    await app.init({
      canvas,
      width: INTERNAL_WIDTH,
      height: INTERNAL_HEIGHT,
      resolution: 1,
      autoDensity: false,
      autoStart: false,
      sharedTicker: false,
      preference: ['webgl'],
      webgl: {
        preferWebGLVersion: 2,
        powerPreference: 'high-performance',
        preserveDrawingBuffer: false,
      },
      antialias: false,
      roundPixels: true,
      backgroundColor: 0x02030a,
      clearBeforeRender: true,
      hello: false,
    });
    if (this.disposed) {
      app.destroy(true);
      return;
    }
    app.ticker.stop();
    canvas.width = INTERNAL_WIDTH;
    canvas.height = INTERNAL_HEIGHT;
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'LUMINA ARCHIVE game display');
    canvas.style.imageRendering = 'pixelated';
    const gl2 = canvas.getContext('webgl2');
    if (!gl2) {
      app.destroy(true);
      throw new Error('WebGL2 is required but unavailable.');
    }
    this.rendererName = `WebGL2 / PixiJS ${INTERNAL_WIDTH}×${INTERNAL_HEIGHT}`;
    this.app = app;
    const sheet = await Assets.load(SPRITE_ATLAS_URL) as AtlasSheet;
    const backgroundSheet = await Assets.load(BACKGROUND_ATLAS_URL) as AtlasSheet;
    if (this.disposed) {
      app.destroy(true);
      void Assets.unload(SPRITE_ATLAS_URL);
      void Assets.unload(BACKGROUND_ATLAS_URL);
      return;
    }
    if (!sheet?.textures || !backgroundSheet?.textures) throw new Error('Generated texture atlases could not be loaded.');
    if (sheet.textureSource) sheet.textureSource.scaleMode = 'nearest';
    if (backgroundSheet.textureSource) backgroundSheet.textureSource.scaleMode = 'nearest';
    for (const texture of Object.values(sheet.textures)) texture.source.scaleMode = 'nearest';
    for (const texture of Object.values(backgroundSheet.textures)) texture.source.scaleMode = 'nearest';
    this.sheet = sheet;
    this.backgroundSheet = backgroundSheet;
    await Assets.load(FONT_URL);
    if (this.disposed) {
      app.destroy(true);
      void Assets.unload(SPRITE_ATLAS_URL);
      void Assets.unload(BACKGROUND_ATLAS_URL);
      void Assets.unload(FONT_URL);
      return;
    }
    this.buildScene();
  }

  setSettings(settings: RendererSettings): void {
    this.settings = settings;
  }

  render(world: GameWorld): void {
    // HOTPATH-BEGIN
    const app = this.app;
    const root = this.root;
    const background = this.background;
    const game = this.gameLayer;
    if (!app || !root || !background || !game || !this.sheet || !this.backgroundSheet) return;
    if (this.currentStageTexture !== world.currentStage) this.applyStageTexture(world.currentStage);
    const scroll = world.scrollX;
    if (this.far) {
      this.far.tilePosition.x = -Math.floor(scroll * 0.08);
      this.far.tilePosition.y = 0;
    }
    if (this.middle) {
      this.middle.tilePosition.x = -Math.floor(scroll * 0.31);
      this.middle.tilePosition.y = 0;
    }
    if (this.near) {
      this.near.tilePosition.x = -Math.floor(scroll * 0.76);
      this.near.tilePosition.y = 0;
    }
    this.updateFilterUniforms(world);
    let shakeX = 0;
    let shakeY = 0;
    if (world.screenShakeTicks > 0 && this.settings.shake > 0) {
      const amount = Math.min(3, world.screenShakeTicks * 0.16) * this.settings.shake;
      shakeX = (((world.simTick * 13) & 7) - 3) * amount * 0.25;
      shakeY = (((world.simTick * 7) & 7) - 3) * amount * 0.2;
    }
    root.x = Math.round(shakeX);
    root.y = Math.round(shakeY);
    this.renderPlayer(world);
    this.renderNodes(world);
    this.renderEnemies(world);
    this.renderProjectiles(world);
    this.renderParticles(world);
    this.renderPickups(world);
    this.renderBoss(world);
    this.renderTether(world);
    this.renderCalibrationGate(world);
    if (this.erasure) {
      this.erasure.visible = world.bosses.active && world.bosses.kind === BossKind.MnemosyneArray && world.bosses.erasure > 0;
      this.erasure.width = Math.floor(INTERNAL_WIDTH * world.bosses.erasure * 0.48);
      this.erasure.height = INTERNAL_HEIGHT;
      this.erasure.x = 0;
      this.erasure.y = 0;
      this.erasure.alpha = 0.88;
    }
    if (this.flash) {
      const flash = this.settings.flashReduction ? Math.min(0.16, world.globalFlashTicks / 80) : Math.min(0.7, world.globalFlashTicks / 18);
      this.flash.visible = flash > 0;
      this.flash.alpha = flash;
    }
    app.renderer.render(app.stage);
    // HOTPATH-END
  }

  dispose(): void {
    this.disposed = true;
    const app = this.app;
    this.app = null;
    this.sheet = null;
    this.backgroundSheet = null;
    this.root = null;
    this.background = null;
    this.gameLayer = null;
    this.effectsLayer = null;
    this.calibrationLayer = null;
    this.far = null;
    this.middle = null;
    this.near = null;
    this.player = null;
    this.erasure = null;
    this.flash = null;
    this.nodes.length = 0;
    this.enemies.length = 0;
    this.projectiles.length = 0;
    this.particles.length = 0;
    this.pickups.length = 0;
    this.bossParts.length = 0;
    this.bossLines.length = 0;
    this.tetherLines.length = 0;
    this.lineFilter = null;
    this.paletteFilter = null;
    this.gateHighlight = null;
    this.gateTitle = null;
    this.gateLux = null;
    this.gateMessage = null;
    this.gateNames.length = 0;
    this.gatePrices.length = 0;
    if (app) app.destroy(true);
    void Assets.unload(SPRITE_ATLAS_URL);
    void Assets.unload(BACKGROUND_ATLAS_URL);
    void Assets.unload(FONT_URL);
  }

  private buildScene(): void {
    const app = this.app;
    const sheet = this.sheet;
    const backgroundSheet = this.backgroundSheet;
    if (!app || !sheet || !backgroundSheet) return;
    const root = new Container();
    const background = new Container();
    const gameLayer = new Container();
    const effectsLayer = new Container();
    const calibrationLayer = new Container();
    root.addChild(background, gameLayer, effectsLayer, calibrationLayer);
    app.stage.addChild(root);
    this.root = root;
    this.background = background;
    this.gameLayer = gameLayer;
    this.effectsLayer = effectsLayer;
    this.calibrationLayer = calibrationLayer;

    const farTexture = backgroundSheet.textures.bg_glass_far ?? Texture.WHITE;
    const middleTexture = backgroundSheet.textures.bg_glass_mid ?? Texture.WHITE;
    const nearTexture = backgroundSheet.textures.bg_glass_near ?? Texture.WHITE;
    this.far = new TilingSprite({ texture: farTexture, width: INTERNAL_WIDTH, height: INTERNAL_HEIGHT });
    this.middle = new TilingSprite({ texture: middleTexture, width: INTERNAL_WIDTH, height: INTERNAL_HEIGHT });
    this.near = new TilingSprite({ texture: nearTexture, width: INTERNAL_WIDTH, height: INTERNAL_HEIGHT });
    this.far.alpha = 1;
    this.middle.alpha = STAGE_ART[0].middleAlpha;
    this.near.alpha = STAGE_ART[0].nearAlpha;
    this.far.tileScale.set(1, 1);
    this.middle.tileScale.set(1, 1);
    this.near.tileScale.set(1, 1);
    background.addChild(this.far, this.middle, this.near);

    this.lineFilter = new Filter({
      glProgram: new GlProgram({ vertex: filterVertex, fragment: lineFragment }),
      resources: {
        lineUniforms: {
          uTime: { value: 0, type: 'f32' },
          uStrength: { value: 0, type: 'f32' },
        },
      },
    });
    this.middle.filters = [this.lineFilter];
    this.paletteFilter = new Filter({
      glProgram: new GlProgram({ vertex: filterVertex, fragment: paletteFragment }),
      resources: {
        paletteUniforms: {
          uPaletteRow: { value: 0, type: 'f32' },
          uPaletteMix: { value: 0, type: 'f32' },
          uDamageFlash: { value: 0, type: 'f32' },
          uGlobalFlash: { value: 0, type: 'f32' },
        },
      },
    });
    this.far.filters = [this.paletteFilter];

    this.erasure = this.makeSprite(Texture.WHITE, 0, 0.5);
    this.erasure.tint = 0x01020a;
    this.erasure.visible = false;
    background.addChild(this.erasure);

    this.player = this.makeSprite(sheet.textures.player_level_0 ?? Texture.WHITE, 0.5, 0.5);
    gameLayer.addChild(this.player);
    for (let i = 0; i < 3; i += 1) {
      const sprite = this.makeSprite(sheet.textures.node_0 ?? Texture.WHITE, 0.5, 0.5);
      this.nodes.push(sprite);
      gameLayer.addChild(sprite);
    }
    for (let i = 0; i < POOL_CAPACITY.enemies; i += 1) {
      const sprite = this.makeSprite(sheet.textures.enemy_threadling_level_0 ?? Texture.WHITE, 0.5, 0.5);
      this.enemies.push(sprite);
      gameLayer.addChild(sprite);
    }
    for (let i = 0; i < POOL_CAPACITY.projectiles; i += 1) {
      const sprite = this.makeSprite(sheet.textures.projectile_pulse ?? Texture.WHITE, 0.5, 0.5);
      this.projectiles.push(sprite);
      gameLayer.addChild(sprite);
    }
    for (let i = 0; i < POOL_CAPACITY.particles; i += 1) {
      const sprite = this.makeSprite(sheet.textures.explosion_0 ?? Texture.WHITE, 0.5, 0.5);
      this.particles.push(sprite);
      effectsLayer.addChild(sprite);
    }
    for (let i = 0; i < POOL_CAPACITY.pickups; i += 1) {
      const sprite = this.makeSprite(sheet.textures.pickup_lux_0 ?? Texture.WHITE, 0.5, 0.5);
      this.pickups.push(sprite);
      gameLayer.addChild(sprite);
    }
    for (let i = 0; i < POOL_CAPACITY.bossParts; i += 1) {
      const sprite = this.makeSprite(sheet.textures.boss_mnemosyne_part_0 ?? Texture.WHITE, 0.5, 0.5);
      this.bossParts.push(sprite);
      gameLayer.addChild(sprite);
    }
    for (let i = 0; i < 8; i += 1) {
      const sprite = this.makeLineSprite(sheet.textures.projectile_beam ?? Texture.WHITE);
      sprite.tint = 0xff3f9b;
      sprite.alpha = 0.5;
      this.bossLines.push(sprite);
      gameLayer.addChild(sprite);
    }
    for (let i = 0; i < 3; i += 1) {
      const sprite = this.makeLineSprite(sheet.textures.projectile_beam ?? Texture.WHITE);
      sprite.tint = 0x50e8ff;
      sprite.alpha = 0.72;
      this.tetherLines.push(sprite);
      gameLayer.addChild(sprite);
    }
    this.flash = this.makeSprite(Texture.WHITE, 0, 0);
    this.flash.width = INTERNAL_WIDTH;
    this.flash.height = INTERNAL_HEIGHT;
    this.flash.tint = 0xffffff;
    this.flash.visible = false;
    effectsLayer.addChild(this.flash);

    const gateShade = this.makeSprite(Texture.WHITE, 0, 0);
    gateShade.visible = true;
    gateShade.width = INTERNAL_WIDTH;
    gateShade.height = INTERNAL_HEIGHT;
    gateShade.tint = 0x02050d;
    gateShade.alpha = 0.88;
    calibrationLayer.addChild(gateShade);
    const gatePanel = this.makeSprite(Texture.WHITE, 0, 0);
    gatePanel.visible = true;
    gatePanel.x = 14;
    gatePanel.y = 12;
    gatePanel.width = 356;
    gatePanel.height = 192;
    gatePanel.tint = 0x0b1930;
    gatePanel.alpha = 0.96;
    calibrationLayer.addChild(gatePanel);
    this.gateHighlight = this.makeSprite(Texture.WHITE, 0, 0);
    this.gateHighlight.visible = true;
    this.gateHighlight.x = 24;
    this.gateHighlight.width = 336;
    this.gateHighlight.height = 15;
    this.gateHighlight.tint = 0x1e6784;
    this.gateHighlight.alpha = 0.72;
    calibrationLayer.addChild(this.gateHighlight);
    this.gateTitle = this.makeBitmapText('CALIBRATION GATE', 24, 20, 12, 0x7ff1ff);
    this.gateLux = this.makeBitmapText('LUX 0000', 274, 22, 8, 0xffdf62);
    this.gateMessage = this.makeBitmapText('UP/DOWN SELECT  ENTER BUY  ESC LEAVE', 24, 185, 7, 0x8aa8c7);
    calibrationLayer.addChild(this.gateTitle, this.gateLux, this.gateMessage);
    for (let i = 0; i < 8; i += 1) {
      const name = this.makeBitmapText('', 30, 48 + i * 17, 8, 0xd6efff);
      const price = this.makeBitmapText('', 274, 48 + i * 17, 8, 0xffdc70);
      this.gateNames.push(name);
      this.gatePrices.push(price);
      calibrationLayer.addChild(name, price);
    }
    calibrationLayer.visible = false;
  }

  private makeBitmapText(text: string, x: number, y: number, size: number, tint: number): BitmapText {
    const value = new BitmapText({
      text,
      style: { fontFamily: 'LuminaPixel', fontSize: size, fill: 0xffffff, letterSpacing: 0 },
    });
    value.x = x;
    value.y = y;
    value.tint = tint;
    value.roundPixels = true;
    return value;
  }

  private makeSprite(texture: Texture, anchorX: number, anchorY: number): Sprite {
    const sprite = new Sprite(texture);
    sprite.anchor.set(anchorX, anchorY);
    sprite.visible = false;
    sprite.roundPixels = true;
    return sprite;
  }

  private makeLineSprite(texture: Texture): Sprite {
    const sprite = this.makeSprite(texture, 0, 0.5);
    sprite.height = 2;
    return sprite;
  }

  private applyStageTexture(stageId: number): void {
    const sheet = this.backgroundSheet;
    if (!sheet) return;
    const safeStage = stageId >= 0 && stageId < STAGE_ART.length ? stageId : 0;
    const art = STAGE_ART[safeStage]!;
    const far = sheet.textures[art.far] ?? Texture.WHITE;
    const middle = sheet.textures[art.middle] ?? Texture.WHITE;
    const near = sheet.textures[art.near] ?? Texture.WHITE;
    if (this.far) {
      this.far.texture = far;
      this.far.alpha = 1;
      this.far.tileScale.set(1, 1);
    }
    if (this.middle) {
      this.middle.texture = middle;
      this.middle.alpha = art.middleAlpha;
      this.middle.tileScale.set(1, 1);
    }
    if (this.near) {
      this.near.texture = near;
      this.near.alpha = art.nearAlpha;
      this.near.tileScale.set(1, 1);
    }
    this.currentStageTexture = safeStage;
  }

  private updateFilterUniforms(world: GameWorld): void {
    if (this.lineFilter) {
      const uniforms = this.lineFilter.resources.lineUniforms.uniforms;
      uniforms.uTime = world.simTick * 0.055;
      uniforms.uStrength = world.currentStage === 0 ? 2 : world.currentStage === 2 ? 1 : 0;
    }
    if (this.paletteFilter) {
      const uniforms = this.paletteFilter.resources.paletteUniforms.uniforms;
      uniforms.uPaletteRow = world.stageDirector.spec.paletteRow;
      uniforms.uPaletteMix = world.currentStage === 3 ? 0.04 : 0.025;
      uniforms.uDamageFlash = 0;
      uniforms.uGlobalFlash = this.settings.flashReduction ? 0 : Math.min(0.08, world.globalFlashTicks / 120);
    }
  }

  private renderPlayer(world: GameWorld): void {
    const sprite = this.player;
    const sheet = this.sheet;
    if (!sprite || !sheet) return;
    sprite.visible = world.player.alive;
    if (!sprite.visible) return;
    const bank = world.player.vy < -0.15 ? 0 : world.player.vy > 0.15 ? 2 : 1;
    const phase = (world.simTick >> 3) & 1;
    const textureName = PLAYER_TEXTURES[bank]![phase]!;
    const texture = sheet.textures[textureName];
    if (texture && sprite.texture !== texture) sprite.texture = texture;
    sprite.anchor.set(0.5, 0.5);
    sprite.x = Math.round(world.player.x);
    sprite.y = Math.round(world.player.y);
    sprite.rotation = 0;
    sprite.scale.set(1, 1);
    sprite.alpha = world.player.invulnerabilityTicks > 0 && ((world.simTick >> 2) & 1) === 0 ? 0.34 : 1;
    sprite.tint = world.nodeDefenseDisabled() ? 0xffb279 : 0xffffff;
  }

  private renderNodes(world: GameWorld): void {
    const sheet = this.sheet!;
    for (let i = 0; i < 3; i += 1) {
      const sprite = this.nodes[i]!;
      sprite.visible = world.player.alive;
      if (!sprite.visible) continue;
      const disabled = world.nodes.disabledTicks[i] > 0;
      // Charge selects an authored silhouette. It is never expressed through
      // alpha or tint: the node art carries charge as shape, and a pink tint
      // would break the shared-palette kinship with VEIL SKIMMER.
      const charge = world.nodes.charge[i]!;
      const chargeFrame = charge < 1 / 3 ? 0 : charge < 2 / 3 ? 1 : 2;
      const textureName = disabled ? NODE_DISABLED_TEXTURE : NODE_TEXTURES[chargeFrame]!;
      const texture = sheet.textures[textureName];
      if (texture && sprite.texture !== texture) sprite.texture = texture;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = Math.round(world.nodes.x[i]);
      sprite.y = Math.round(world.nodes.y[i]);
      sprite.rotation = 0;
      sprite.scale.set(1, 1);
      sprite.alpha = 1;
      sprite.tint = 0xffffff;
    }
  }

  private renderEnemies(world: GameWorld): void {
    const pool = world.enemies;
    const sheet = this.sheet!;
    for (let i = 0; i < this.enemies.length; i += 1) {
      const sprite = this.enemies[i]!;
      const active = pool.indices.active[i] !== 0;
      sprite.visible = active;
      if (!active) continue;
      const bank = pool.vy[i] < -0.16 ? 0 : pool.vy[i] > 0.16 ? 2 : 1;
      const phase = ((world.simTick >> 4) + i + pool.variant[i]) & 1;
      const typeFrames = ENEMY_TEXTURES[pool.type[i]] ?? ENEMY_TEXTURES[0];
      const textureName = typeFrames[bank * 2 + phase]!;
      const texture = sheet.textures[textureName];
      if (texture && sprite.texture !== texture) sprite.texture = texture;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = Math.round(pool.x[i]);
      sprite.y = Math.round(pool.y[i]);
      sprite.rotation = 0;
      sprite.scale.set(pool.vx[i] > 0.08 ? -1 : 1, 1);
      sprite.alpha = pool.state[i] === EnemyState.Telegraph
        ? 0.46 + ((world.simTick >> 2) & 1) * 0.46
        : pool.state[i] === EnemyState.Dying
          ? Math.max(0, pool.stateTimer[i] / 18)
          : pool.state[i] === EnemyState.Spawn
            ? 0.68
            : 1;
      sprite.tint = pool.state[i] === EnemyState.Disabled ? 0x8292ad : 0xffffff;
    }
  }

  private renderProjectiles(world: GameWorld): void {
    const pool = world.projectiles;
    const sheet = this.sheet!;
    for (let i = 0; i < this.projectiles.length; i += 1) {
      const sprite = this.projectiles[i]!;
      const active = pool.indices.active[i] !== 0;
      sprite.visible = active;
      if (!active) continue;
      const segment = (pool.flags[i] & ProjectileFlags.Segment) !== 0;
      let textureName: string = PROJECTILE_TEXTURES[pool.kind[i]] ?? PROJECTILE_TEXTURES[0];
      if (pool.kind[i] === ProjectileKind.Node) textureName = NODE_PROJECTILE_TEXTURES[world.player.lens] ?? NODE_PROJECTILE_TEXTURES[0];
      const texture = sheet.textures[textureName] ?? Texture.WHITE;
      if (sprite.texture !== texture) sprite.texture = texture;
      if (segment) {
        this.placeLine(sprite, pool.x[i], pool.y[i], pool.x2[i], pool.y2[i], pool.kind[i] === ProjectileKind.Laser ? 3 : 2);
        sprite.tint = pool.owner[i] === Owner.Enemy ? 0xff4f87 : 0x70fff2;
        sprite.alpha = 0.9;
      } else {
        sprite.anchor.set(0.5, 0.5);
        sprite.x = Math.round(pool.x[i]);
        sprite.y = Math.round(pool.y[i]);
        sprite.rotation = Math.round(pool.angle[i] / ANGLE_STEP) * ANGLE_STEP;
        sprite.scale.set(1, 1);
        sprite.tint = 0xffffff;
        sprite.alpha = 1;
      }
    }
  }

  private renderParticles(world: GameWorld): void {
    const pool = world.particles;
    const sheet = this.sheet!;
    for (let i = 0; i < this.particles.length; i += 1) {
      const sprite = this.particles[i]!;
      const active = pool.indices.active[i] !== 0;
      sprite.visible = active;
      if (!active) continue;
      const remaining = pool.life[i] / Math.max(1, pool.maxLife[i]);
      let frameIndex: number;
      if (pool.size[i] >= 2.2) {
        frameIndex = Math.min(EXPLOSION_TEXTURES.length - 1, Math.floor((1 - remaining) * EXPLOSION_TEXTURES.length));
      } else {
        frameIndex = 5 + ((pool.frame[i] + (world.simTick >> 2)) % 3);
      }
      const texture = sheet.textures[EXPLOSION_TEXTURES[frameIndex]!];
      if (texture && sprite.texture !== texture) sprite.texture = texture;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = Math.round(pool.x[i]);
      sprite.y = Math.round(pool.y[i]);
      sprite.rotation = 0;
      const scale = pool.size[i] >= 2.2 ? 1 : 0.5;
      sprite.scale.set(scale, scale);
      sprite.alpha = Math.max(0, remaining);
      sprite.tint = 0xffffff;
    }
  }

  private renderPickups(world: GameWorld): void {
    const pool = world.pickups;
    const sheet = this.sheet!;
    for (let i = 0; i < this.pickups.length; i += 1) {
      const sprite = this.pickups[i]!;
      const active = pool.indices.active[i] !== 0;
      sprite.visible = active;
      if (!active) continue;
      const phase = ((world.simTick >> 4) + i) & 1;
      const textureName = pool.kind[i] === 1 ? REPAIR_TEXTURES[phase]! : LUX_TEXTURES[phase]!;
      const texture = sheet.textures[textureName];
      if (texture && sprite.texture !== texture) sprite.texture = texture;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = Math.round(pool.x[i]);
      sprite.y = Math.round(pool.y[i]) + (((world.simTick >> 3) + i) & 1);
      sprite.rotation = 0;
      sprite.scale.set(1, 1);
      sprite.alpha = ((world.simTick >> 3) + i) & 1 ? 1 : 0.86;
      sprite.tint = 0xffffff;
    }
  }

  private renderBoss(world: GameWorld): void {
    const pool = world.bossParts;
    const sheet = this.sheet!;
    const phase = (world.simTick >> 4) & 1;
    for (let i = 0; i < this.bossParts.length; i += 1) {
      const sprite = this.bossParts[i]!;
      const active = world.bosses.active && pool.indices.active[i] !== 0 && pool.visible[i] !== 0;
      sprite.visible = active;
      if (!active) continue;
      let textureName: string;
      if (world.bosses.kind === BossKind.CatalogueMouth) {
        textureName = pool.partType[i] === 0 ? CATALOGUE_JAW_TEXTURES[phase]! : CATALOGUE_CORE_TEXTURES[phase]!;
      } else if (world.bosses.kind === BossKind.NullScribe) {
        textureName = pool.partType[i] === 2 ? NULL_SCRIBE_CORE_TEXTURES[phase]! : NULL_SCRIBE_QUILL_TEXTURES[phase]!;
      } else {
        textureName = i === world.bosses.corePart ? MNEMOSYNE_CORE_TEXTURES[phase]! : MNEMOSYNE_PART_TEXTURES[phase]!;
      }
      const texture = sheet.textures[textureName];
      if (texture && sprite.texture !== texture) sprite.texture = texture;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = Math.round(pool.x[i]);
      sprite.y = Math.round(pool.y[i]);
      sprite.rotation = Math.round(pool.angle[i] / ANGLE_STEP) * ANGLE_STEP;
      sprite.scale.set(1, 1);
      sprite.alpha = pool.vulnerable[i] !== 0 ? 1 : 0.74;
      sprite.tint = pool.vulnerable[i] !== 0 ? 0xffffff : 0x8190a8;
    }
    for (let i = 0; i < this.bossLines.length; i += 1) {
      const sprite = this.bossLines[i]!;
      const active = world.bosses.active && world.bosses.lineState[i] === 1;
      sprite.visible = active;
      if (!active) continue;
      this.placeLine(sprite, world.bosses.lineX1[i], world.bosses.lineY1[i], world.bosses.lineX2[i], world.bosses.lineY2[i], 2);
      sprite.alpha = 0.3 + ((world.simTick >> 2) & 1) * 0.4;
    }
  }

  private renderTether(world: GameWorld): void {
    const active = world.nodes.tetherActiveTicks > 0;
    for (let i = 0; i < 3; i += 1) {
      const sprite = this.tetherLines[i]!;
      sprite.visible = active;
      if (!active) continue;
      const next = (i + 1) % 3;
      this.placeLine(sprite, world.nodes.x[i], world.nodes.y[i], world.nodes.x[next], world.nodes.y[next], 2);
      sprite.alpha = 0.5 + Math.sin(world.simTick * 0.4 + i) * 0.2;
    }
  }

  private renderCalibrationGate(world: GameWorld): void {
    const layer = this.calibrationLayer;
    if (!layer) return;
    const visible = world.mode === GameMode.CalibrationGate;
    layer.visible = visible;
    if (!visible) return;
    let hash = 0;
    for (let i = 0; i < world.player.purchaseCounts.length; i += 1) hash = ((hash * 33) ^ world.player.purchaseCounts[i]) >>> 0;
    hash = ((hash * 33) ^ world.player.core) >>> 0;
    hash = ((hash * 33) ^ world.player.lens) >>> 0;
    hash = ((hash * 33) ^ world.player.device) >>> 0;
    hash = ((hash * 33) ^ world.player.lives) >>> 0;
    hash = ((hash * 33) ^ world.player.maxLives) >>> 0;
    const messageState = world.gateMessageTicks > 0 ? (world.lastPurchaseSucceeded ? 2 : 1) : 0;
    if (this.gateCacheSelection === world.gateSelection && this.gateCacheLux === world.player.lux && this.gateCacheHash === hash && this.gateCacheMessageState === messageState) return;
    this.gateCacheSelection = world.gateSelection;
    this.gateCacheLux = world.player.lux;
    this.gateCacheHash = hash;
    this.gateCacheMessageState = messageState;
    const page = world.gateSelection >> 3;
    const start = page * 8;
    if (this.gateHighlight) this.gateHighlight.y = 45 + (world.gateSelection - start) * 17;
    if (this.gateTitle) this.gateTitle.text = `CALIBRATION GATE  ${page + 1}/2`;
    if (this.gateLux) this.gateLux.text = `LUX ${world.player.lux.toString().padStart(4, '0')}`;
    for (let row = 0; row < 8; row += 1) {
      const itemId = start + row;
      const item = PURCHASES[itemId];
      const name = this.gateNames[row]!;
      const price = this.gatePrices[row]!;
      if (!item) {
        name.text = '';
        price.text = '';
        continue;
      }
      const selected = itemId === world.gateSelection;
      name.text = `${selected ? '>' : ' '} ${item.name}`;
      name.tint = selected ? 0xffffff : 0xb8cee4;
      const state = getPurchaseState(world.player, item, world.difficulty);
      const cost = purchaseCost(world.player, item, world.difficulty);
      if (state === PurchaseState.Equip) price.text = 'EQUIP';
      else if (state === PurchaseState.Equipped) price.text = 'EQUIPPED';
      else if (state === PurchaseState.Maxed) price.text = 'MAX';
      else if (state === PurchaseState.RepairNotNeeded) price.text = 'FULL';
      else price.text = `${cost} LUX`;
      price.tint = state === PurchaseState.Equip || state === PurchaseState.Buy ? 0xffdc70 : state === PurchaseState.InsufficientLux ? 0x69778f : 0x72a3b0;
    }
    if (this.gateMessage) {
      if (world.gateMessageTicks > 0) {
        this.gateMessage.text = world.lastPurchaseSucceeded ? 'CALIBRATION APPLIED' : 'CALIBRATION REJECTED';
        this.gateMessage.tint = world.lastPurchaseSucceeded ? 0x72ffb1 : 0xff6d83;
      } else {
        this.gateMessage.text = 'UP/DOWN SELECT  ENTER BUY  ESC LEAVE';
        this.gateMessage.tint = 0x8aa8c7;
      }
    }
  }

  private placeLine(sprite: Sprite, x1: number, y1: number, x2: number, y2: number, thickness: number): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    sprite.anchor.set(0, 0.5);
    sprite.x = Math.round(x1);
    sprite.y = Math.round(y1);
    sprite.rotation = Math.atan2(dy, dx);
    sprite.width = Math.max(1, Math.round(Math.sqrt(dx * dx + dy * dy)));
    sprite.height = Math.max(1, Math.round(thickness));
  }
}
